import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';
import logger from '../utils/logger';
import { notificationService } from '../utils/notifications';

// Circuit breaker për të shmangur kërkesat e përsëritura
const circuitBreaker = {
  failures: 0,
  lastFailureTime: null,
  isOpen: false,
  resetAfter: 30000, // 30 sekonda

  recordFailure() {
    this.failures++;
    this.lastFailureTime = Date.now();
    if (this.failures >= 3) {
      this.isOpen = true;
    }
  },

  recordSuccess() {
    this.failures = 0;
    this.isOpen = false;
    this.lastFailureTime = null;
  },

  canAttempt() {
    if (!this.isOpen) return true;
    if (Date.now() - this.lastFailureTime > this.resetAfter) {
      this.isOpen = false;
      this.failures = 0;
      return true;
    }
    return false;
  }
};

/**
 * Custom hook për API calls me loading dhe error states
 * 
 * @param {Function} apiFunction - Funksioni API që duhet ekzekutuar
 * @param {Array} dependencies - Dependencies për useEffect (si useCallback dependencies)
 * @param {Object} options - Options: { immediate: true, onSuccess, onError, showNotifications }
 */
export const useApi = (apiFunction, dependencies = [], options = {}) => {
  const {
    immediate = true,
    onSuccess,
    onError,
    showNotifications = true,
    retryOnError = false,
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);
  const retryCountRef = useRef(0);
  const lastRequestRef = useRef(null);
  const isExecutingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  const execute = useCallback(async (...args) => {
    // Parandalon ekzekutimin e përsëritur
    if (isExecutingRef.current) {
      return data;
    }

    // Kontrollo circuit breaker
    if (!circuitBreaker.canAttempt()) {
      const error = new Error('Serveri nuk është i disponueshëm. Ju lutem provoni më vonë.');
      error.isCircuitOpen = true;
      setError(error.message);
      setLoading(false);
      return null;
    }

    isExecutingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await apiFunction(...args);
      
      // Kontrollo nëse ka të dhëna valide
      if (result === null || result === undefined) {
        setData(null);
        setLoading(false);
        return null;
      }

      setData(result);
      circuitBreaker.recordSuccess();
      retryCountRef.current = 0;
      
      if (onSuccess) {
        onSuccess(result);
      }

      return result;
    } catch (err) {
      const errorMessage = err.message || 'Një gabim ka ndodhur';
      setError(errorMessage);
      
      // Rrit numrin e dështimeve në circuit breaker
      if (err.isNetworkError) {
        circuitBreaker.recordFailure();
      }

      logger.error('API Error:', err);

      // Shfaq notifikim vetëm nëse nuk është circuit breaker error dhe nuk ka shumë dështime
      if (showNotifications && !err.isCircuitOpen && circuitBreaker.failures < 3) {
        notificationService.error(errorMessage);
      }

      if (onError) {
        onError(err);
      }

      // Retry vetëm nëse nuk është network error dhe nuk ka kaluar limit
      if (retryOnError && !err.isNetworkError && retryCountRef.current < 2) {
        retryCountRef.current++;
        setTimeout(() => execute(...args), 1000 * retryCountRef.current);
        return;
      }

      // Nëse showNotifications është false, mos throw error për të shmangur unhandled promise rejections
      // Vetëm kthe null dhe lë error në state
      if (!showNotifications) {
        return null;
      }

      throw err;
    } finally {
      setLoading(false);
      isExecutingRef.current = false;
    }
  }, [apiFunction, onSuccess, onError, showNotifications, retryOnError, data]);

  useEffect(() => {
    if (immediate && apiFunction && !isExecutingRef.current) {
      // Ekzekuto vetëm një herë në fillim ose kur dependencies ndryshojnë
      if (!hasInitializedRef.current || dependencies.length > 0) {
        execute();
        hasInitializedRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immediate, ...dependencies]);

  return {
    data,
    loading,
    error,
    execute,
    setData, // Për manual updates
  };
};

/**
 * Custom hook për API mutation (POST, PUT, DELETE)
 */
export const useMutation = (apiFunction, options = {}) => {
  const {
    onSuccess,
    onError,
    showNotifications = true,
  } = options;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isExecutingRef = useRef(false);

  const mutate = useCallback(async (...args) => {
    // Parandalon ekzekutimin e përsëritur
    if (isExecutingRef.current) {
      return;
    }

    // Kontrollo circuit breaker
    if (!circuitBreaker.canAttempt()) {
      const error = new Error('Serveri nuk është i disponueshëm. Ju lutem provoni më vonë.');
      error.isCircuitOpen = true;
      setError(error.message);
      return;
    }

    isExecutingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await apiFunction(...args);
      
      circuitBreaker.recordSuccess();
      
      if (showNotifications && options.successMessage) {
        notificationService.success(options.successMessage);
      }

      if (onSuccess) {
        onSuccess(result, ...args);
      }

      return result;
    } catch (err) {
      // Format validation errors properly
      let errorMessage = err.message || 'Një gabim ka ndodhur';
      
      // Nëse ka validation errors, format ato në një mesazh të lexueshëm
      if (err.errors && typeof err.errors === 'object') {
        const errorMessages = [];
        Object.keys(err.errors).forEach(key => {
          const fieldErrors = Array.isArray(err.errors[key]) 
            ? err.errors[key] 
            : [err.errors[key]];
          errorMessages.push(...fieldErrors);
        });
        
        if (errorMessages.length > 0) {
          errorMessage = errorMessages.join(', ');
          // Update the error message on the error object itself
          err.message = errorMessage;
          err.formattedMessage = errorMessage;
        }
      }
      
      setError(errorMessage);
      
      // Rrit numrin e dështimeve në circuit breaker
      if (err.isNetworkError) {
        circuitBreaker.recordFailure();
      }
      
      logger.error('Mutation Error:', err);

      // Shfaq notifikim vetëm nëse nuk është circuit breaker error
      if (showNotifications && !err.isCircuitOpen && !err.isNetworkError) {
        notificationService.error(errorMessage);
      }

      if (onError) {
        try {
          onError(err, ...args);
        } catch (onErrorErr) {
          // If onError throws, log it but don't let it propagate
          logger.error('Error in onError callback:', onErrorErr);
        }
      }

      // Only throw error if it hasn't been handled by onError
      // This prevents React from showing uncaught errors when we've already handled them
      // But we still throw for unhandled cases
      const formattedError = new Error(errorMessage);
      formattedError.errors = err.errors;
      formattedError.response = err.response;
      formattedError.isNetworkError = err.isNetworkError;
      formattedError.isCircuitOpen = err.isCircuitOpen;
      formattedError.formattedMessage = errorMessage;
      
      // Throw error - but it should be caught by the component's error handling
      throw formattedError;
    } finally {
      setLoading(false);
      isExecutingRef.current = false;
    }
  }, [apiFunction, onSuccess, onError, showNotifications, options.successMessage]);

  return {
    mutate,
    execute: mutate, // Alias për konsistencë me useApi
    loading,
    error,
  };
};

